import { evaluate, maxPurchasePrice, scenarios, verdict, type Deal, type Result, type Verdict } from '../lib/flip';
import { percent, pounds } from '../lib/format';
import type { Settings } from '../lib/types';

const VERDICT: Record<Verdict, { label: string; text: string }> = {
  good: { label: 'Worth doing', text: 'Meets your profit, return and speed targets.' },
  marginal: { label: 'Marginal', text: 'Misses some of your targets. Haggle harder, or pass.' },
  avoid: { label: 'Avoid', text: 'Little or no profit, or it misses every target.' },
};

const profitClass = (n: number) => (n > 0 ? 'pos' : n < 0 ? 'neg' : '');

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <tr className={strong ? 'strong' : undefined}>
      <th scope="row">{label}</th>
      <td>{value}</td>
    </tr>
  );
}

interface Props {
  deal: Deal;
  settings: Settings;
}

export function ResultsPanel({ deal, settings }: Props) {
  const r: Result = evaluate(deal);
  const v = verdict(r, settings.targets);
  const sc = scenarios(deal, settings.scenarios);
  const maxOffer = maxPurchasePrice(deal, settings.targets.minProfit);
  const hasSale = deal.salePrice > 0 && deal.purchasePrice > 0;

  return (
    <aside className="results" aria-label="Results">
      <div className={`verdict ${v}`} role="status" aria-live="polite">
        <strong>{hasSale ? VERDICT[v].label : 'Fill in the price and sale price'}</strong>
        <span>{hasSale ? VERDICT[v].text : 'The result updates as you type.'}</span>
      </div>

      <div className="headline">
        <div><span className="label">Profit</span><span className={`big ${profitClass(r.profit)}`}>{pounds(r.profit)}</span></div>
        <div><span className="label">Return on cash</span><span className="big">{percent(r.roiPct)}</span></div>
        <div><span className="label">Profit per day</span><span className="big">{pounds(r.profitPerDay)}</span></div>
      </div>

      <table className="breakdown">
        <caption className="sr-only">Where the money goes</caption>
        <tbody>
          <Row label="Purchase price" value={pounds(deal.purchasePrice)} />
          <Row label="Buying fees" value={pounds(r.buyerFees)} />
          <Row label="Repairs (with allowance)" value={pounds(r.repairsTotal)} />
          <Row label="MOT, valet, checks, tax, advert, collection" value={pounds(r.otherCosts)} />
          <Row label="Cash you put in" value={pounds(r.cashInvested)} strong />
          <Row label="Insurance and storage" value={pounds(r.runningCosts)} />
          <Row label="Cost of tied-up cash" value={pounds(r.capitalCost)} />
          <Row label="Selling fees" value={pounds(r.saleFees)} />
          <Row label="Total cost" value={pounds(r.totalCosts)} strong />
          <Row label="Sale price" value={pounds(deal.salePrice)} />
        </tbody>
      </table>

      <div className="callouts">
        <p>
          <span className="label">Break-even sale price</span>
          <strong>{pounds(r.breakEvenSalePrice)}</strong>
          <small>Sell for less than this and you lose money.</small>
        </p>
        <p>
          <span className="label">Most you should pay</span>
          <strong>{maxOffer > 0 ? pounds(maxOffer) : 'Nothing: this deal cannot reach your target'}</strong>
          <small>For a profit of {pounds(settings.targets.minProfit)} with everything else as entered.</small>
        </p>
      </div>

      <h3>If things go differently</h3>
      <table className="scenarios">
        <thead>
          <tr><th scope="col">Case</th><th scope="col">Profit</th><th scope="col">Return</th></tr>
        </thead>
        <tbody>
          {([['Cautious', sc.cautious], ['Expected', sc.expected], ['Good', sc.good]] as const).map(([name, x]) => (
            <tr key={name}>
              <th scope="row">{name}</th>
              <td className={profitClass(x.profit)}>{pounds(x.profit)}</td>
              <td>{percent(x.roiPct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted small">
        Cautious: sells {Math.abs(settings.scenarios.cautious.salePricePct)}% lower, repairs {settings.scenarios.cautious.repairsPct}% dearer,
        {' '}{settings.scenarios.cautious.extraDays} days slower. Change this in Settings.
      </p>
      {sc.cautious.profit < 0 && hasSale && (
        <p className="warn" role="note">
          In the cautious case you lose {pounds(-sc.cautious.profit)}. Only put in cash you can afford to lose.
        </p>
      )}
    </aside>
  );
}
